export interface CuratedProduct {
    id: string;
    model: string;
    category: string;
    tagline: string;
    description: string;
    image: string;
    accentColor: string;
    bgGradient: string;
    idealFor: string;
    hotspots?: {
        x: number;
        y: number;
        title: string;
        desc: string;
    }[];
    quickStats: {
        label: string;
        value: string;
        subtext: string;
        icon: "heart" | "battery" | "weight" | "water";
    }[];
    specs: {
        battery: { title: string; desc: string };
        sensors: { title: string; desc: string };
        material: { title: string; desc: string };
        connectivity: { title: string; desc: string };
    };
    fullSpecsTable: {
        title: string;
        items: { key: string; val: string }[];
    }[];
    marketPresentation: {
        headline: string;
        uspTitle: string;
        uspItems: {
            title: string;
            description: string;
            tag: string;
        }[];
        guaranteeBadges: string[];
        marketVerdict: string;
    };
}

export interface CuratedShowcaseTexts {
    bandHeader: {
        titleRegular: string;
        titleHighlighted: string;
        description: string;
    };
    ringHeader: {
        titleRegular: string;
        titleHighlighted: string;
        description: string;
    };
    tabs: {
        specs: string;
        marketing: string;
    };
    idealForTitle: string;
    fullSpecs: {
        open: string;
        close: string;
    };
    ctaCompare: string;
    ctaDetails: string;
    zoomLabel: string;
    hotspotToggle: {
        show: string;
        hide: string;
    };
    zoomModalTitle: string;
    zoomModalClose: string;
    zoomModalCompare: string;
    featureBadges: {
        iranServices: string;
        installments: string;
        giftPackage: string;
    };
}

export const SHOWCASE_UI_TEXTS: Record<string, CuratedShowcaseTexts> = {
    fa: {
        bandHeader: {
            titleRegular: "معرفی و بررسی منتخب",
            titleHighlighted: "دستبندهای هوشمند دلبند",
            description: "بررسی عمیق، مشخصات فنی و ارزش خرید ۴ مدل برگزیده برای انتخاب آگاهانه و متناسب با سبک زندگی شما",
        },
        ringHeader: {
            titleRegular: "معرفی و بررسی منتخب",
            titleHighlighted: "حلقه‌های هوشمند دلبند",
            description: "بررسی عمیق، مشخصات فنی و ارزش خرید ۴ مدل برگزیده حلقه برای انتخاب آگاهانه و متناسب با سبک زندگی شما",
        },
        tabs: {
            specs: "مشخصات فنی و سنسورها",
            marketing: "مزیت‌های کلیدی و ارزش خرید",
        },
        idealForTitle: "ایده‌آل برای چه کسانی است؟",
        fullSpecs: {
            open: "مشاهده کامل تمامی مشخصات و جزییات فنی این مدل",
            close: "بستن جدول مشخصات کامل فنی",
        },
        ctaCompare: "بررسی و مقایسه در جدول مشخصات",
        ctaDetails: "مشاهده و بررسی مشخصات",
        zoomLabel: "مشاهده تصویر بزرگ",
        hotspotToggle: {
            show: "نمایش سنسورها",
            hide: "مخفی‌سازی سنسورها",
        },
        zoomModalTitle: "نمای بزرگ‌نمایی و جزئیات",
        zoomModalClose: "بستن",
        zoomModalCompare: "انتقال به جدول مقایسه مشخصات",
        featureBadges: {
            iranServices: "امکان اتصال به سرویس‌های ایرانی",
            installments: "پرداخت قسطی",
            giftPackage: "امکان تهیه بسته‌بندی هدیه",
        },
    },
    en: {
        bandHeader: {
            titleRegular: "Curated Showcase & Review of",
            titleHighlighted: "Delband Smart Bands",
            description: "In-depth breakdown, technical specifications, and key advantages of 4 curated models tailored to your lifestyle",
        },
        ringHeader: {
            titleRegular: "Curated Showcase & Review of",
            titleHighlighted: "Delband Smart Rings",
            description: "In-depth breakdown, technical specifications, and key advantages of 4 curated smart rings tailored to your lifestyle",
        },
        tabs: {
            specs: "Technical Specs & Sensors",
            marketing: "Key Advantages & Purchase Value",
        },
        idealForTitle: "Ideal For Whom?",
        fullSpecs: {
            open: "View Full Technical Specifications & Details",
            close: "Close Full Specifications Table",
        },
        ctaCompare: "Compare in Specifications Table",
        ctaDetails: "Explore Model Specifications",
        zoomLabel: "Click to Enlarge",
        hotspotToggle: {
            show: "Show Sensors",
            hide: "Hide Sensors",
        },
        zoomModalTitle: "High-Resolution Details View",
        zoomModalClose: "Close",
        zoomModalCompare: "Go to Specifications Comparison",
        featureBadges: {
            iranServices: "Compatible with Local Services",
            installments: "Installment Payment",
            giftPackage: "Gift Packaging Available",
        },
    },
    ar: {
        bandHeader: {
            titleRegular: "العرض الحصري والمراجعة لـ",
            titleHighlighted: "أساور دلبند الذكية",
            description: "مراجعة تفصيلية، المواصفات الفنية والمزايا الحصرية لأربعة طرز مختارة لتناسب أسلوب حياتك",
        },
        ringHeader: {
            titleRegular: "العرض الحصري والمراجعة لـ",
            titleHighlighted: "خواتم دلبند الذكية",
            description: "مراجعة تفصيلية، المواصفات الفنية والمزايا الحصرية لأربعة خواتم مختارة لتناسب أسلوب حياتك",
        },
        tabs: {
            specs: "المواصفات الفنية والمستشعرات",
            marketing: "المزايا الرئيسية وقيمة الشراء",
        },
        idealForTitle: "مثالي لمن؟",
        fullSpecs: {
            open: "عرض كامل المواصفات والتفاصيل الفنية لهذا الطراز",
            close: "إغلاق جدول المواصفات الفنية الكاملة",
        },
        ctaCompare: "المقارنة في جدول المواصفات",
        ctaDetails: "استعراض تفاصيل الطراز",
        zoomLabel: "عرض الصورة الكبيرة",
        hotspotToggle: {
            show: "عرض المستشعرات",
            hide: "إخفاء المستشعرات",
        },
        zoomModalTitle: "عرض التكبير والتفاصيل الدقيقة",
        zoomModalClose: "إغلاق",
        zoomModalCompare: "الانتقال إلى جدول المقارنة",
        featureBadges: {
            iranServices: "إمكانية الاتصال بالخدمات المحلية",
            installments: "الدفع بالتقسيط",
            giftPackage: "إمكانية توفير تغليف الهدايا",
        },
    },
    tr: {
        bandHeader: {
            titleRegular: "Özel Tanıtım ve İnceleme:",
            titleHighlighted: "Delband Akıllı Bileklikler",
            description: "Yaşam tarzınıza uygun bilinçli bir seçim için özenle seçilmiş 4 modelin teknik özellikleri ve avantajları",
        },
        ringHeader: {
            titleRegular: "Özel Tanıtım ve İnceleme:",
            titleHighlighted: "Delband Akıllı Yüzükler",
            description: "Yaşam tarzınıza uygun bilinçli bir seçim için özenle seçilmiş 4 akıllı yüzüğün teknik özellikleri ve avantajları",
        },
        tabs: {
            specs: "Teknik Özellikler ve Sensörler",
            marketing: "Temel Avantajlar ve Değer",
        },
        idealForTitle: "Kimler İçin İdeal?",
        fullSpecs: {
            open: "Tüm Teknik Özellikleri ve Detayları Gör",
            close: "Tam Teknik Özellikler Tablosunu Kapat",
        },
        ctaCompare: "Özellikler Tablosunda Karşılaştır",
        ctaDetails: "Model Özelliklerini İncele",
        zoomLabel: "Büyük Görseli İncele",
        hotspotToggle: {
            show: "Sensörleri Göster",
            hide: "Sensörleri Gizle",
        },
        zoomModalTitle: "Yüksek Çözünürlüklü Detay Görünümü",
        zoomModalClose: "Kapat",
        zoomModalCompare: "Karşılaştırma Tablosuna Git",
        featureBadges: {
            iranServices: "Yerel Servislere Bağlanabilirlik",
            installments: "Taksitli Ödeme Seçeneği",
            giftPackage: "Hediye Paketi Seçeneği",
        },
    },
};

// ─── Persian Products ────────────────────────────────────────────────────────
const BAND_PRODUCTS_FA: CuratedProduct[] = [
    {
        id: "EVB-X8",
        model: "EVB-X8",
        category: "پرچمدار پایش سلامت و تناسب اندام",
        tagline: "مجهز به نوار قلب ۵۱۲ هرتزی، ارزیابی غیرتهاجمی روند انرژی و گردش خون و دمای پیوسته پوست",
        description:
            "پیشرفته‌ترین دستبند سلامت بدون نمایشگر دلبند با ثبت تخصصی امواج ECG، پایش ۲۴ ساعته تغییرات گردش خون و روند انرژی، سنسور حرارتی پوست، استاندارد IP68 و شارژدهی ۱۵+ روز بدون حواس‌پرتی.",
        image: "/media/products/band/EVB-X8.webp",
        accentColor: "#0038A8",
        bgGradient: "from-blue-100/40 via-indigo-50/30 to-white",
        idealFor: "افرادی که به دنبال کامل‌ترین پایشگر سلامت، ارزیابی دقیق روند انرژی و گردش خون، و تحلیل جامع خواب هستند.",
        hotspots: [
            { x: 50, y: 32, title: "تراشه الکترود ECG ۵۱۲Hz", desc: "ثبت دقیق فرکانس و شکل موج نوار قلب استاندارد" },
            { x: 45, y: 60, title: "سنسور اپتیکال گردش خون و انرژی", desc: "ارزیابی غیرتهاجمی روند انرژی و گردش خون روزانه" },
            { x: 65, y: 76, title: "سنسور دمای پیوسته پوست", desc: "ثبت تغییرات حرارت بدن با دقت ±۰.۰۵°C" },
        ],
        quickStats: [
            { label: "فرکانس ECG", value: "۵۱۲ هرتز", subtext: "ثبت شکل موج استاندارد", icon: "heart" },
            { label: "دوام باتری", value: "۱۵+ روز", subtext: "پایش پیوسته شبانه‌روزی", icon: "battery" },
            { label: "سنسورها", value: "۸ حسگر", subtext: "ECG، SpO2، انرژی، گردش، دما", icon: "weight" },
            { label: "مقاومت", value: "IP68", subtext: "ضد آب و غبار کامل", icon: "water" },
        ],
        specs: {
            battery: { title: "بیش از ۱۵ روز شارژدهی مداوم", desc: "باتری ۱۸۰ میلی‌آمپر با مدار مصرف بهینه و شارژ مگنتی ۶۰ دقیقه‌ای" },
            sensors: { title: "تراشه ECG + سنجش روند انرژی و گردش خون", desc: "نوار قلب، برآورد غیرتهاجمی انرژی و گردش خون، SpO2، HRV و دمای پوست" },
            material: { title: "پلی‌کربنات فشرده + بندهای متنوع", desc: "بدنه بدون حساسیت با بندهای بافت‌دار (Woven) تنفسی و سیلیکونی" },
            connectivity: { title: "بلوتوث ۵.۳ کم‌مصرف + اشتراک ابری", desc: "همگام‌سازی زنده داده‌ها با اپلیکیشن دلبند و اعضای خانواده" },
        },
        fullSpecsTable: [
            {
                title: "سخت‌افزار و حسگرها",
                items: [
                    { key: "تراشه نوار قلب ECG", val: "الکترودهای تماسی ۵۱۲ هرتزی با خروجی استاندارد شکل‌موج" },
                    { key: "سنسورهای نوری PPG", val: "ماژول دوکاناله با پایش مداوم ضربان قلب و SpO2" },
                    { key: "ارزیابی روند انرژی و گردش خون", val: "الگوریتم‌های پیشرفته تحلیل روند انرژی و گردش خون" },
                    { key: "سنسور دمای پوست", val: "پایش پیوسته حرارت سطحی پوست با دقت ±۰.۰۵°C" },
                ],
            },
            {
                title: "باتری، بدنه و اتصالات",
                items: [
                    { key: "ظرفیت باتری", val: "۱۸۰ میلی‌آمپرساعت لیتیوم-پلیمر با شارژدهی ۱۵+ روز" },
                    { key: "زمان شارژ", val: "۶۰ دقیقه (شارژ سریع مغناطیسی)" },
                    { key: "استاندارد مقاومت", val: "IP68 (مقاوم در برابر نفوذ آب، استخر و گردوغبار)" },
                    { key: "پشتیبانی نرم‌افزاری", val: "اندروید و iOS با پشتیبانی کامل از زبان فارسی و اپ دلبند" },
                ],
            },
        ],
        marketPresentation: {
            headline: "کامل‌ترین مرکز پایش سلامت و خودمراقبتی روی مچ دست شما",
            uspTitle: "مزیت‌های کلیدی EVB-X8",
            uspItems: [
                {
                    title: "پایش ۲۴ ساعته بدون حواس‌پرتی",
                    description: "طراحی بدون نمایشگر که آرامش ذهنی را به ارمغان آورده و تمام داده‌های حیاتی را با بالاترین دقت ذخیره می‌کند.",
                    tag: "تمرکز بر سلامت",
                },
                {
                    title: "نوار قلب ۵۱۲ هرتزی رده تخصصی",
                    description: "امکان ثبت نوار قلب استاندارد و ارائه گزارش‌های تحلیلی تحلیلی جهت خودمراقبتی.",
                    tag: "دقت بالا",
                },
                {
                    title: "کامل‌ترین بسته سنسورهای پایش سلامت",
                    description: "ترکیب نوار قلب، روند انرژی، گردش خون، دمای پوست و SpO2 در یک گجت سبک.",
                    tag: "فول سنسور",
                },
            ],
            guaranteeBadges: [
                "۱۸ ماه گارانتی طلایی تعویض دلبند",
                "۷ روز ضمانت بازگشت وجه بی‌قید و شرط",
                "پشتیبانی نرم‌افزاری و به‌روزرسانی مادام‌العمر",
            ],
            marketVerdict: "بهترین انتخاب برای افرادی که پایش همه‌جانبه سلامت قلب، متابولیسم و آمادگی جسمانی را می‌خواهند.",
        },
    },
    {
        id: "EVB-E50",
        model: "EVB-E50",
        category: "پایش قلبی-عروقی با آلیاژ روی مقاوم و نوار قلب الکترودی",
        tagline: "ثبت الکترودهای نوار قلب ECG، پایش شاخص‌های حیاتی و متابولیسم با بدنه آلیاژ فلزی و باتری ۱۴ روزه",
        description:
            "دستبند سلامت متالیک بدون نمایشگر ساخته‌شده از ترکیب آلیاژ روی و پلی‌کربنات (Zinc Alloy + PC)؛ مجهز به الکترودهای ECG و سنسور اپتیکال دوگانه PPG برای پایش مداوم ضربان، شاخص‌های حیاتی، روند انرژی و خواب با شارژدهی ۷ تا ۱۴ روز و آماده‌باش ۳۰+ روز.",
        image: "/media/hero-band/band3.webp",
        accentColor: "#0D9488",
        bgGradient: "from-teal-100/40 via-emerald-50/30 to-white",
        idealFor: "کاربرانی که خواهان کیفیت ساخت متالیک، نوار قلب دقیق، ارزیابی شاخص‌های قلبی-عروقی و تمرکز کامل بدون حواس‌پرتی هستند.",
        hotspots: [
            { x: 50, y: 35, title: "الکترود تماسی ECG", desc: "ثبت مستقیم امواج نوار قلب با لمس بدنه آلیاژی" },
            { x: 45, y: 62, title: "سنسور نوری PPG دوگانه", desc: "سنجش مداوم SpO2، گردش خون و ریتم قلبی" },
            { x: 65, y: 78, title: "بدنه آلیاژ روی (Zinc Alloy)", desc: "مقاوم در برابر سایش، ضربه و ضدحساسیت" },
        ],
        quickStats: [
            { label: "سنسور ECG", value: "الکترودی", subtext: "ثبت نوار قلب دقیق", icon: "heart" },
            { label: "دوام باتری", value: "۱۴ روز", subtext: "آماده‌باش تا ۳۰+ روز", icon: "battery" },
            { label: "متریال", value: "Zinc Alloy", subtext: "آلیاژ روی + PC تقویت‌شده", icon: "weight" },
            { label: "استاندارد", value: "IP68", subtext: "مقاوم در برابر آب و تعریق", icon: "water" },
        ],
        specs: {
            battery: { title: "۷ تا ۱۴ روز شارژدهی (آماده‌باش ۳۰+ روز)", desc: "باتری ۱۳۰ میلی‌آمپرساعت با مصرف بهینه به دلیل طراحی بدون نمایشگر" },
            sensors: { title: "الکترودهای ECG + سنسورهای PPG", desc: "نوار قلب، پایش شاخص‌های حیاتی، ارزیابی غیرتهاجمی روند انرژی، دمای پوست و خواب" },
            material: { title: "آلیاژ روی و پلی‌کربنات فشرده", desc: "بدنه مقاوم و شیک همراه با بندهای پارچه‌ای بافت‌دار و سیلیکونی" },
            connectivity: { title: "بلوتوث ۵.۰ کم‌مصرف", desc: "اتصال پایدار و انتقال خودکار سوابق سلامت به اپلیکیشن دلبند" },
        },
        fullSpecsTable: [
            {
                title: "سنسورها و تجهیزات پایش سلامت",
                items: [
                    { key: "سیستم نوار قلب", val: "الکترودهای تماسی ECG با پردازشگر سیگنال بیومتریک" },
                    { key: "پایش شاخص‌های حیاتی و متابولیسم", val: "ارزیابی غیرتهاجمی روند انرژی و آمادگی جسمانی" },
                    { key: "سنسور حرارتی", val: "پایش پیوسته دمای پوست" },
                    { key: "پایش خواب و تنفس", val: "پایش فازهای خواب، کیفیت استراحت و نرخ تنفس" },
                ],
            },
            {
                title: "مشخصات ساختاری و باتری",
                items: [
                    { key: "جنس بدنه", val: "آلیاژ روی باکیفیت (Zinc Alloy) + بدنه پایینی پلی‌کربنات" },
                    { key: "ظرفیت باتری", val: "۱۳۰ میلی‌آمپرساعت لیتیوم پلیمری با شارژ مگنتی" },
                    { key: "مقاومت در برابر آب", val: "استاندارد IP68 ضد آب و تعریق" },
                    { key: "پشتیبانی بانوان", val: "پایش کامل چرخه قاعدگی، تخمک‌گذاری و علائم بارداری" },
                ],
            },
        ],
        marketPresentation: {
            headline: "تلفیق استحکام آلیاژ فلزی با پایش پیشرفته شاخص‌های قلب و متابولیسم",
            uspTitle: "ویژگی‌های منحصربه‌فرد EVB-E50",
            uspItems: [
                {
                    title: "نوار قلب الکترودی و پایش ۲۴/۷",
                    description: "ثبت آسان و دقیق نوار قلب با قرار دادن انگشت روی الکترودهای بدنه فلزی.",
                    tag: "الکترود تماسی",
                },
                {
                    title: "کیفیت ساخت ممتاز متالیک",
                    description: "آلیاژ روی با پوشش ضدحساسیت که حس پرمیوم و طول عمر بالا را تضمین می‌کند.",
                    tag: "بدنه فلزی بادوام",
                },
                {
                    title: "شارژدهی طولانی تا ۱۴ روز",
                    description: "بهینه‌سازی حداکثری مصرف انرژی با شارژدهی فعال دو هفته‌ای و آماده‌باش ۳۰ روزه.",
                    tag: "باتری قدرتمند",
                },
            ],
            guaranteeBadges: [
                "۱۸ ماه گارانتی رسمی دلبند",
                "۷ روز مهلت تست و تعویض",
                "پشتیبانی فنی و راهنمای کامل فارسی",
            ],
            marketVerdict: "دستبندی شیک و مقاوم برای کسانی که نوار قلب، بدنه فلزی و دوام شارژ بالا می‌خواهند.",
        },
    },
    {
        id: "EVB-X5",
        model: "EVB-X5",
        category: "استقلال شارژ ۳۰ روزه با داک مغناطیسی همراه",
        tagline: "همراهی ۳۰ روزه با داک شارژ هوشمند، پایش تخصصی کیفیت خواب و استرس",
        description:
            "طراحی شده برای سبک زندگی پرتحرک و سفر؛ همراه با داک شارژ هوشمند ۲۱۰ میلی‌آمپرساعتی که نیاز به پریز برق را برای یک ماه کامل برطرف می‌کند؛ مجهز به پایش ۲۴/۷ ضربان، SpO2، پایش گردش خون، دمای پوست و آنالیز خواب.",
        image: "/media/products/band/band-4.webp",
        accentColor: "#0284C7",
        bgGradient: "from-cyan-100/40 via-blue-50/30 to-white",
        idealFor: "مسافران، کوهنوردان و افرادی که دوام باتری فوق‌العاده، داک شارژ پرتابل و آنالیز عمیق خواب و استرس را می‌خواهند.",
        hotspots: [
            { x: 50, y: 35, title: "داک مغناطیسی ۲۱۰mAh", desc: "پاوربانک پرتابل اختصاصی برای ۳۰ روز بی‌نیازی از پریز" },
            { x: 45, y: 60, title: "حسگر خواب شبانه", desc: "ثبت دقیق فازهای REM، عمیق و سبک" },
            { x: 65, y: 75, title: "سیلیکون باکیفیت", desc: "مقاوم در برابر تعریق، اشعه و شست‌وشوی مکرر" },
        ],
        quickStats: [
            { label: "دوام با داک", value: "۳۰ روز", subtext: "همراه با کیس شارژ ۲۱۰mAh", icon: "battery" },
            { label: "آنالیز خواب", value: "۴ مرحله", subtext: "ثبت دقیق خواب سبک، عمیق و REM", icon: "heart" },
            { label: "استاندارد غوطه‌وری", value: "IP68", subtext: "مقاوم در برابر شنا و استفاده روزمره", icon: "water" },
            { label: "ردیابی استرس", value: "پیوسته", subtext: "تمرین‌های تنفسی هدایت‌شده", icon: "weight" },
        ],
        specs: {
            battery: { title: "۳۰ روز با داک شارژ همراه", desc: "داک مگنتی کوچک که به عنوان پاوربانک اختصاصی دستبند عمل می‌کند" },
            sensors: { title: "PPG دوکاناله با فیلتر نویز", desc: "پایش پیوسته اکسیژن خون در خواب و تحلیل نظم تنفس" },
            material: { title: "سیلیکون باکیفیت ضدحساسیت", desc: "مقاوم در برابر حساسیت پوستی، تعریق شدید و اشعه UV" },
            connectivity: { title: "همگام‌سازی آفلاین", desc: "ذخیره داده‌های ۷ روز بدون نیاز به اتصال دائم به موبایل" },
        },
        fullSpecsTable: [
            {
                title: "سیستم شارژ و پاوربانک همراه",
                items: [
                    { key: "داک شارژ همراه", val: "کیس مغناطیسی ۲۱۰ میلی‌آمپرساعت با قابلیت شارژ مجدد دستبند" },
                    { key: "مجموع شارژدهی", val: "تا ۳۰ روز استفاده مداوم بدون اتصال به آداپتور برق" },
                    { key: "پورت ورودی کیس", val: "پورت مدرن Type-C با مدار محافظت از تخلیه بیش‌ازحد" },
                ],
            },
            {
                title: "پایش خواب و تنفس",
                items: [
                    { key: "شناسایی مراحل خواب", val: "تفکیک خواب سبک، عمیق، REM و بیداری‌های ناخودآگاه" },
                    { key: "پایش الگوی تنفس خواب", val: "شناسایی افت ناگهانی SpO2 و نوسانات نرخ تنفس شبانه" },
                    { key: "حافظه داخلی آفلاین", val: "ذخیره سوابق زیستی ۷ روز بدون نیاز به نزدیکی موبایل" },
                ],
            },
        ],
        marketPresentation: {
            headline: "آزادی عمل کامل در سفر و زندگی پرتحرک با استقلال شارژ ۱ ماهه",
            uspTitle: "چرا مسافران و کوهنوردان عاشق EVB-X5 می‌شوند؟",
            uspItems: [
                {
                    title: "داک شارژ مگنتی پرتابل",
                    description: "دیگر نگران جا گذاشتن کابل یا خاموش شدن دستبند در سفرها، کمپینگ و ماموریت‌های طولانی نباشید.",
                    tag: "استقلال انرژی بی‌نظیر",
                },
                {
                    title: "آنالیز تخصصی و دقیق خواب (Sleep Lab)",
                    description: "ثبت دقیق‌ترین نمره خواب و تحلیل الگوی تنفس شبانه.",
                    tag: "پایش عمیق ریکاوری",
                },
                {
                    title: "مقاومت و دوام در سخت‌ترین شرایط",
                    description: "بدنه با استاندارد IP68 و سیلیکون مقاوم به گرد و غبار، اشعه خورشید و آب.",
                    tag: "طراحی آماده سفر",
                },
            ],
            guaranteeBadges: [
                "۱۸ ماه گارانتی معتبر دلبند",
                "پشتیبانی فنی در سراسر کشور",
                "بسته‌بندی مسافرتی لوکس همراه با داک شارژ",
            ],
            marketVerdict: "بهترین انتخاب برای افراد پرمشغله و ورزشکاران فضای باز که به دوام شارژ بالا اهمیت می‌دهند.",
        },
    },
    {
        id: "EVB-1s",
        model: "EVB-1s",
        category: "مربی هوش مصنوعی، بار تمرینی (Strain) و ریکاوری پیشرفته",
        tagline: "آنالیز هوشمند استرین و ریکاوری با هوش مصنوعی، سنجش VO2Max و پایش خواب عمیق",
        description:
            "دستبند نسل جدید و ارگونومیک بدون نمایشگر با ابعاد ۴۲×۲۲×۹.۵ میلی‌متر؛ مجهز به الگوریتم‌های هوش مصنوعی برای محاسبه بار تمرینی (Strain)، نمره ریکاوری روزانه، پایش فازهای خواب (REM/Deep) و کیفیت تنفس شبانه، سنجش VO2Max، دمای پوست، استرس و حالات روحی (AI Mood).",
        image: "/media/hero-band/EVB-1s.webp",
        accentColor: "#6366F1",
        bgGradient: "from-indigo-100/40 via-purple-50/30 to-white",
        idealFor: "ورزشکاران حرفه‌ای، علاقه‌مندان به بهینه‌سازی انرژی روزانه، خواب باکیفیت و ارتقای ریکاوری عصبی و جسمانی.",
        hotspots: [
            { x: 50, y: 35, title: "موتور هوش مصنوعی ریکاوری", desc: "محاسبه بار تمرینی Strain و نمره آمادگی روزانه" },
            { x: 45, y: 62, title: "سنسورهای نوری اپتیکال و دما", desc: "پایش پیوسته ضربان، SpO2، HRV و دمای پوست" },
            { x: 65, y: 76, title: "بند بافت‌دار کشسان تنفسی", desc: "فوق‌العاده سبک و بدون حس سنگینی روی مچ" },
        ],
        quickStats: [
            { label: "مربی ریکاوری", value: "هوش مصنوعی", subtext: "سنجش بار تمرینی Strain و Mood", icon: "heart" },
            { label: "دوام باتری", value: "۷ تا ۱۰ روز", subtext: "آماده‌باش ۲۵+ روز", icon: "battery" },
            { label: "سنسورها", value: "VO2Max", subtext: "پایش خواب، تنفس و دما", icon: "weight" },
            { label: "استاندارد", value: "IP68", subtext: "مقاوم در برابر آب و تعریق", icon: "water" },
        ],
        specs: {
            battery: { title: "۷ تا ۱۰ روز شارژدهی فعال", desc: "باتری ۹۵ میلی‌آمپرساعت با فست‌شارژ مغناطیسی سریع در ۶۰ دقیقه" },
            sensors: { title: "سنسورهای پیشرفته با هوش مصنوعی", desc: "ضربان قلب پیوسته، HRV، SpO2، دمای پوست، VO2Max و کیفیت تنفس شبانه" },
            material: { title: "بدنه ارگونومیک PC+ABS", desc: "وزن بسیار سبک با ابعاد ۴۲×۲۲×۹.۵mm و بند بافت‌دار ضدحساسیت" },
            connectivity: { title: "بلوتوث ۵.۰ کم‌مصرف", desc: "اتصال هوشمند به اپلیکیشن با تحلیل اختصاصی ریکاوری و سبک زندگی" },
        },
        fullSpecsTable: [
            {
                title: "هوش مصنوعی و سنسورهای زیستی",
                items: [
                    { key: "محاسبه استرین و ریکاوری", val: "الگوریتم هوشمند محاسبه Strain روزانه و شاخص آمادگی ریکاوری" },
                    { key: "سنجش توان هوازی VO2Max", val: "تخمین سطح آمادگی قلبی-عروقی و ظرفیت جذب اکسیژن" },
                    { key: "پایش خواب و کیفیت تنفس", val: "تفکیک مراحل خواب عمیق/REM و پایش الگوی تنفس شبانه" },
                    { key: "پایش حالات روحی و استرس", val: "تحلیل الگوهای HRV و ثبت وضعیت روحی (AI Mood)" },
                ],
            },
            {
                title: "طراحی، ابعاد و باتری",
                items: [
                    { key: "ابعاد فیزیکی", val: "۴۲ × ۲۲ × ۹.۵ میلی‌متر (فوق‌العاده باریک و سبک)" },
                    { key: "ظرفیت باتری", val: "۹۵ میلی‌آمپرساعت با ماندگاری ۷ تا ۱۰ روز (آماده‌باش ۲۵+ روز)" },
                    { key: "مقاومت و استاندارد", val: "استاندارد ضد آب و گردوغبار IP68" },
                    { key: "پشتیبانی بانوان", val: "پایش کامل چرخه قاعدگی، تخمک‌گذاری و ریکاوری" },
                ],
            },
        ],
        marketPresentation: {
            headline: "مربی هوشمند ریکاوری و توازن زیستی برای ارتقای توانایی جسمانی و ذهنی",
            uspTitle: "مزیت‌های کلیدی EVB-1s برای ورزش و سبک زندگی",
            uspItems: [
                {
                    title: "الگوریتم پیشرفته محاسبه Strain و آمادگی ریکاوری",
                    description: "میزان فشار تمرینی و استرس روزانه را با کیفیت ریکاوری شبانه تطبیق داده و بهترین زمان تمرین را پیشنهاد می‌دهد.",
                    tag: "مربی هوش مصنوعی",
                },
                {
                    title: "تحلیل عمیق فازهای خواب و الگوی تنفس",
                    description: "پایش فازهای سبک، عمیق و REM خواب به همراه بررسی نوسانات اکسیژن و الگوی تنفس شبانه.",
                    tag: "پایش خواب و تنفس",
                },
                {
                    title: "ارگونومی عالی و وزن پر مانند",
                    description: "با ضخامت تنها ۹.۵ میلی‌متر و بند کشسان بافت‌دار، هیچ مزاحمتی روی دست احساس نخواهید کرد.",
                    tag: "طراحی ارگونومیک",
                },
            ],
            guaranteeBadges: [
                "۱۸ ماه گارانتی معتبر دلبند",
                "ارسال رایگان و مهلت تست ۷ روزه",
                "همگام‌سازی با تمام پلتفرم‌های مطرح ورزشی",
            ],
            marketVerdict: "بهترین انتخاب برای ورزشکاران و افرادی که مربی هوشمند ریکاوری، بار تمرینی و سنجش VO2Max می‌خواهند.",
        },
    },
];

const RING_PRODUCTS_FA: CuratedProduct[] = [
    {
        id: "EVR-X5",
        model: "EVR-X5",
        category: "حلقه هوشمند لمسی با بدنه سرامیک زیرکونیا و تیتانیوم",
        tagline: "کنترل لمسی هوشمند (Smart Touch)، پایش شاخص‌های متابولیسم و اکسیژن خون، بدنه سرامیکی ضدخش و شارژ بی‌سیم",
        description:
            "حلقه هوشمند پیشگام EVR-X5 با ضخامت بسیار باریک ۲.۲ میلی‌متر، ترکیب سرامیک نانو زیرکونیا و تیتانیوم، مجهز به کنترل لمسی هوشمند برای مدیریت محتوا، اسلایدها و عکاسی، پایش ۲۴ ساعته ضربان قلب، اکسیژن خون، تغییرات HRV، ارزیابی غیرتهاجمی روند انرژی و متابولیسم، دمای پوست و ۵۴+ حالت ورزشی تخصصی همراه با ۵ تا ۱۰ روز شارژدهی.",
        image: "/media/products/rings/EVR-X5.webp",
        accentColor: "#D97706",
        bgGradient: "from-amber-100/40 via-orange-50/30 to-white",
        idealFor: "علاقه‌مندان به فناوری‌های نوین، کنترل لمسی هوشمند ابزارها، متریال فوق‌العاده لوکس ضدخش و پایش کامل سلامت شخصی.",
        hotspots: [
            { x: 50, y: 32, title: "سنسور کنترل لمسی هوشمند", desc: "کنترل شاتر دوربین، اسلایدها و موزیک با لمس سطح حلقه" },
            { x: 46, y: 60, title: "حسگر اپتیکال PPG و اکسیژن خون", desc: "پایش پیوسته SpO2، ضربان قلب و شاخص انرژی" },
            { x: 64, y: 76, title: "بدنه سرامیک نانو زیرکونیا", desc: "کاملاً ضدخش، براق و ضدحساسیت با مقاومت 5ATM" },
        ],
        quickStats: [
            { label: "کنترل لمسی", value: "Smart Touch", subtext: "ژست لمسی و سوایپ", icon: "weight" },
            { label: "ضخامت بدنه", value: "۲.۲ mm", subtext: "فوق‌العاده باریک و سبک", icon: "heart" },
            { label: "ضد آب", value: "5ATM", subtext: "مقاوم تا ۵۰ متر عمق", icon: "water" },
            { label: "شارژدهی", value: "۵ تا ۱۰ روز", subtext: "شارژ سریع بی‌سیم", icon: "battery" },
        ],
        specs: {
            battery: { title: "۵ تا ۱۰ روز شارژدهی پیوسته", desc: "شارژ بی‌سیم سریع مغناطیسی در کمتر از ۹۰ دقیقه" },
            sensors: { title: "کنترل لمسی + PPG و سنجش شاخص‌های متابولیسم", desc: "پایش ضربان، SpO2، شاخص‌های انرژی غیرتهاجمی، HRV و دمای پوست" },
            material: { title: "سرامیک زیرکونیا صیقلی + تیتانیوم", desc: "پوشش داخلی رزین آنتی‌باکتریال ۱۰۰٪ هایپوآلرژیک و ضدحساسیت" },
            connectivity: { title: "بلوتوث ۵.۱ کم‌مصرف + کنترل لمسی", desc: "همگام‌سازی ابری و تعامل با گوشی، دوربین و ارائه‌ها" },
        },
        fullSpecsTable: [
            {
                title: "سنسورها، ژست‌های لمسی و پردازش",
                items: [
                    { key: "کنترل لمسی (Touch Control)", val: "حسگر لمسی خازنی روی بدنه برای اسکرول ویدیو، تغییر اسلاید و شاتر دوربین" },
                    { key: "پایش اپتیکال بیومتریک", val: "سنسور PPG دوکاناله مادون‌قرمز و قرمز با سنجش SpO2 و ضربان" },
                    { key: "تحلیل غیرتهاجمی روند انرژی", val: "الگوریتم برآورد نوسانات شاخص انرژی و سلامت متابولیک" },
                    { key: "مودهای ورزشی و VO2Max", val: "بیش از ۵۴ حالت ورزشی هوشمند با سنجش توان هوازی VO2Max" },
                ],
            },
            {
                title: "طراحی بدنه، باتری و اتصالات",
                items: [
                    { key: "جنس بدنه خارجی", val: "سرامیک نانوکریستال زیرکونیا با سختی الماس / تیتانیوم ضدخش" },
                    { key: "ابعاد و ضخامت", val: "ضخامت ۲.۲ میلی‌متر | پهنا ۸.۰ میلی‌متر (ارگونومی فوق‌العاده باریک)" },
                    { key: "استاندارد مقاومت آب", val: "گواهی 5ATM (مقاوم تا عمق ۵۰ متری، مناسب شنا و حمام)" },
                    { key: "باتری و شارژ", val: "باتری لیتیوم-پلیمر با دوام ۵ تا ۱۰ روز و شارژر بی‌سیم القایی" },
                ],
            },
        ],
        marketPresentation: {
            headline: "تعامل لمسی جادویی با دنیای دیجیتال در قالب حلقه‌ای سرامیکی و بی‌نهایت باریک",
            uspTitle: "ویژگی‌های بی‌رقیب EVR-X5",
            uspItems: [
                {
                    title: "کنترل لمسی هوشمند (Smart Touch)",
                    description: "تنها با لمس یا کشیدن انگشت روی حلقه، ویدیوها را ورق بزنید، موسیقی را متوقف کنید یا عکس بگیرید.",
                    tag: "تعامل لمسی",
                },
                {
                    title: "فوق‌العاده باریک با ضخامت ۲.۲ میلی‌متر",
                    description: "باریک‌ترین حلقه هوشمند با روکش سرامیکی آینه‌ای که روی انگشت مانند یک جواهر لوکس می‌درخشد.",
                    tag: "طراحی مینیاتوری",
                },
                {
                    title: "پایش ۲۴ ساعته شاخص متابولیسم و ۵۴+ حالت ورزشی",
                    description: "برآورد روند انرژی، اکسیژن خون، تغییرات HRV و توان هوازی VO2Max بدون نیاز به دستگاه‌های دیگر.",
                    tag: "پایش چندوجهی",
                },
            ],
            guaranteeBadges: [
                "۱۸ ماه گارانتی معتبر دلبند",
                "کیت رایگان سایزبندی انگشت",
                "پد شارژ بی‌سیم سریع در بسته‌بندی",
            ],
            marketVerdict: "بهترین انتخاب برای کسانی که خواهان ترکیب کنترل لمسی مدرن، زیبایی سرامیکی و پایش جامع سلامت هستند.",
        },
    },
    {
        id: "EVR-X3",
        model: "EVR-X3",
        category: "پایش پیشرفته اکسیژن خون و کیفیت تنفس خواب",
        tagline: "سنسور ترانسمیسیون پیشرفته SpO2 با دقت ±۲.۷٪، ارزیابی کیفیت تنفس خواب، تیتانیوم هوافضا و پایش روند انرژی",
        description:
            "حلقه هوشمند تخصصی EVR-X3 ساخته‌شده از آلیاژ تیتانیوم هوافضایی با وزن تنها ۳.۴ گرم و ضخامت ۲.۷ میلی‌متر، مجهز به حسگر ترانسمیسیون اکسیژن خون با استاندارد پیشرفته، ارزیابی شاخص وقفه تنفسی در خواب، تحلیل روند شاخص‌های متابولیسم، مقاومت آب 5ATM و شارژدهی بیش از ۷ روز با شارژ بی‌سیم پرسرعت کمتر از ۹۰ دقیقه.",
        image: "/media/products/rings/EVR-X3.webp",
        accentColor: "#0284C7",
        bgGradient: "from-cyan-100/40 via-blue-50/30 to-white",
        idealFor: "افراد دارای خروپف و مشکوک به وقفه‌های تنفسی، ورزشکاران و علاقه‌مندان به پایش دقیق اکسیژن.",
        hotspots: [
            { x: 48, y: 34, title: "حسگر ترانسمیسیون SpO2", desc: "سنجش اکسیژن خون با دقت پیشرفته ±۲.۷٪ و پایش تنفس خواب" },
            { x: 52, y: 60, title: "ماژول روند انرژی و متابولیسم", desc: "ارزیابی غیرتهاجمی روند انرژی و متابولیسم" },
            { x: 62, y: 76, title: "آلیاژ تیتانیوم هوافضایی", desc: "وزن ۳.۴ گرم، مقاوم در برابر خط و خش و ضدآب 5ATM" },
        ],
        quickStats: [
            { label: "دقت اکسیژن", value: "±۲.۷٪", subtext: "سطح پیشرفته و پایش تنفس خواب", icon: "heart" },
            { label: "وزن خالص", value: "۳.۴ گرم", subtext: "ضخامت بسیار ظریف ۲.۷mm", icon: "weight" },
            { label: "مقاومت آب", value: "5ATM", subtext: "شنا و غواصی تا ۵۰ متر", icon: "water" },
            { label: "شارژدهی", value: "۷+ روز", subtext: "شارژ بی‌سیم فوق‌سریع", icon: "battery" },
        ],
        specs: {
            battery: { title: "بیش از ۷ روز کارکرد فعال", desc: "فناوری شارژ سریع بی‌سیم القایی در کمتر از ۹۰ دقیقه" },
            sensors: { title: "SpO2 ترانسمیسیون + پایش روند انرژی", desc: "سنسور اکسیژن خون تخصصی، شاخص کیفیت تنفس، دمای پوست و HRV" },
            material: { title: "آلیاژ تیتانیوم گرید هوافضا", desc: "سبک، مقاوم، هایپوآلرژیک و ضدحساسیت با پوشش مات و براق" },
            connectivity: { title: "بلوتوث ۵.۲ با اتصال پایدار", desc: "انتقال امن داده‌ها و هشدارهای تنفسی به اپلیکیشن دلبند" },
        },
        fullSpecsTable: [
            {
                title: "سنسورهای رده پیشرفته و پایش تنفس",
                items: [
                    { key: "حسگر اکسیژن ترانسمیسیون", val: "سنسور تخصصی عبور نور با دقت پیشرفته ±۲.۷٪" },
                    { key: "پایش شاخص تنفس خواب", val: "تشخیص افت‌های مکرر اکسیژن شبانه و ارزیابی کیفیت تنفس حین خواب" },
                    { key: "ماژول روند انرژی و متابولیسم", val: "ارزیابی غیرتهاجمی و هوشمند وضعیت الگوی متابولیک بر پایه داده‌های فوتوپلتیسموگرافی" },
                    { key: "پایش دمای پیوسته پوست", val: "سنجش مداوم نوسانات حرارتی بدن و تشخیص تغییرات بیولوژیک" },
                ],
            },
            {
                title: "مشخصات فنی، بدنه و دوام",
                items: [
                    { key: "متریال بدنه", val: "آلیاژ تیتانیوم هوافضا (Titanium) با پوشش رزین آنتی‌باکتریال داخلی" },
                    { key: "وزن و ابعاد", val: "وزن ۳.۴ گرم | ضخامت ۲.۷ میلی‌متر (بسیار باریک و راحت)" },
                    { key: "مقاومت در برابر آب", val: "گواهی 5ATM (مقاوم تا ۵۰ متر عمق آب، مناسب شنا)" },
                    { key: "حالت‌های ورزشی", val: "بیش از ۵۴ حالت ورزشی تخصصی + محاسبه VO2Max" },
                ],
            },
        ],
        marketPresentation: {
            headline: "دقیق‌ترین پایشگر اکسیژن خون و سلامت تنفسی شبانه در جهان",
            uspTitle: "مزیت‌های منحصر‌به‌فرد EVR-X3",
            uspItems: [
                {
                    title: "سنسور ترانسمیسیون SpO2 با دقت بالا",
                    description: "برخلاف سنسورهای انعکاسی معمولی، با عبور نور از بافت مویرگی انگشت بالاترین دقت سنجش اکسیژن را ارائه می‌دهد.",
                    tag: "دقت بالا",
                },
                {
                    title: "تشخیص وقفه تنفسی شبانه",
                    description: "پایش لحظه‌ای افت اکسیژن در خواب برای محافظت و آگاهی سالمندان و افراد دارای خروپف.",
                    tag: "ایمنی تنفس خواب",
                },
                {
                    title: "تیتانیوم فوق‌سبک ۳.۴ گرمی با ۷ روز باتری",
                    description: "طراحی هوافضایی بدون ایجاد مزاحمت روی انگشت همراه با شارژ بی‌سیم سریع ۹۰ دقیقه‌ای.",
                    tag: "سبک و با دوام",
                },
            ],
            guaranteeBadges: [
                "۱۸ ماه گارانتی طلایی دلبند",
                "ارسال کیت سایزبندی رایگان",
                "گزارش تخصصی سلامت خواب و تنفس در اپلیکیشن",
            ],
            marketVerdict: "تخصصی‌ترین و ایده‌آل‌ترین حلقه هوشمند برای پایش دقیق اکسیژن، تنفس خواب و علائم حیاتی.",
        },
    },
    {
        id: "EVR-X2",
        model: "EVR-X2",
        category: "آزمایشگاه خواب شبانه و ریکاوری پیوسته",
        tagline: "ماژول مادون‌قرمز اختصاصی خواب، پایش دقیق فازهای REM و عمیق، نمره ریکاوری HRV و بدنه تیتانیومی",
        description:
            "حلقه هوشمند EVR-X2 با ارگونومی بهینه و بدنه تیتانیومی سبک، به‌طور تخصصی برای پایش پیوسته خواب و ریکاوری شبانه‌روزی طراحی شده است. این حلقه با رهگیری دقیق مراحل خواب (سبک، عمیق، REM)، دمای پوست، نوسانات اکسیژن شبانه، ۵۴+ حالت ورزشی و شارژدهی ۵ تا ۷ روز، تصویری کامل از کیفیت استراحت و ریکاوری شما ارائه می‌دهد.",
        image: "/media/products/rings/EVR-X2.webp",
        accentColor: "#6366F1",
        bgGradient: "from-indigo-100/40 via-purple-50/30 to-white",
        idealFor: "افرادی که به دنبال خواب عمیق‌تر، بهبود ریکاوری بدنی و ذهنی، کاهش استرس و اکسسوری راحت بدون نور مزاحم هستند.",
        hotspots: [
            { x: 48, y: 35, title: "ماژول مادون‌قرمز خواب", desc: "ثبت پیوسته فازهای خواب بدون انتشار نور مرئی مزاحم در شب" },
            { x: 54, y: 58, title: "حسگر دمای پوست و HRV", desc: "محاسبه نمره آمادگی زیستی و ریکاوری عصبی روزانه" },
            { x: 62, y: 76, title: "بدنه ارگونومیک تیتانیومی", desc: "پهنای ۸.۰ میلی‌متر با احساس راحتی مطلق حین خواب" },
        ],
        quickStats: [
            { label: "پایش خواب", value: "تخصصی", subtext: "فازهای ۴ گانه + نمره ریکاوری", icon: "heart" },
            { label: "متریال", value: "تیتانیوم", subtext: "سبک، بادوام و هایپوآلرژیک", icon: "weight" },
            { label: "مقاومت آب", value: "5ATM", subtext: "ضدآب تا ۵۰ متر", icon: "water" },
            { label: "باتری", value: "۵ تا ۷ روز", subtext: "شارژ بی‌سیم بدون وقفه", icon: "battery" },
        ],
        specs: {
            battery: { title: "۵ تا ۷ روز پایش مداوم شبانه‌روزی", desc: "پد شارژ بی‌سیم مگنتی سریع با مصرف بهینه‌سازی‌شده" },
            sensors: { title: "ماژول مادون‌قرمز PPG + سنسور دما", desc: "پایش فازهای عمیق و REM خواب، HRV، اکسیژن و ضربان" },
            material: { title: "تیتانیوم سبک و مقاوم با پوشش صیقلی", desc: "پوشش داخلی رزین آنتی‌باکتریال ۱۰۰٪ ضدحساسیت مناسب پوست‌های حساس" },
            connectivity: { title: "بلوتوث کم‌مصرف + پایش خودکار", desc: "همگام‌سازی بی‌صدا در پس‌زمینه بدون نیاز به باز کردن دائمی اپ" },
        },
        fullSpecsTable: [
            {
                title: "تحلیل تخصصی خواب و سیستم عصبی",
                items: [
                    { key: "پایش فازهای خواب", val: "ثبت خواب سبک (Light)، خواب عمیق (Deep)، فاز رویا (REM) و دفعات بیداری" },
                    { key: "نمره ریکاوری و انرژی (Fuel Score)", val: "تحلیل همزمان کیفیت استراحت، HRV و آمادگی بدنی برای شروع روز" },
                    { key: "نوسانات اکسیژن خواب (SpO2)", val: "بررسی افت غلظت اکسیژن خون در ساعات شبانه" },
                    { key: "سلامت بانوان", val: "پایش دمای شبانه، پیش‌بینی دوره‌های قاعدگی و پنجره باروری" },
                ],
            },
            {
                title: "ساختار ارگونومیک، باتری و ضدآب",
                items: [
                    { key: "جنس بدنه", val: "آلیاژ تیتانیوم صیقلی در رنگ‌های مشکی، نقره‌ای، خاکستری و طلایی" },
                    { key: "ابعاد و وزن", val: "پهنا ۸.۰ میلی‌متر | وزن سبک حدود ۳ گرم" },
                    { key: "مقاومت در برابر آب", val: "استاندارد 5ATM (مقاوم در برابر دوش آب، شستشو، استخر و باران)" },
                    { key: "حالت‌های ورزشی", val: "پشتیبانی از ۵۴+ رشته ورزشی و سنجش VO2Max" },
                ],
            },
        ],
        marketPresentation: {
            headline: "آزمایشگاه خواب شخصی شما در قالبی شیک و فوق‌العاده راحت",
            uspTitle: "چرا EVR-X2 بهترین همراه خواب شبانه است؟",
            uspItems: [
                {
                    title: "نور نامرئی مادون‌قرمز بدون اختلال در خواب",
                    description: "برخلاف ساعت‌ها با نور سبز تند، پایش خواب با نور نامرئی مادون‌قرمز انجام می‌شود تا خوابی عمیق داشته باشید.",
                    tag: "پایش نامرئی",
                },
                {
                    title: "تحلیل دقیق فازهای عمیق، REM و ریکاوری",
                    description: "نمودارهای شفاف و دقیق از مدت زمان خواب عمیق، ترمیم سلولی و آمادگی عصبی برای روز بعد.",
                    tag: "کیفیت استراحت",
                },
                {
                    title: "بدنه تیتانیومی سبک با شارژ هفتگی",
                    description: "یک بار شارژ در هفته برای پایش شبانه‌روزی تمام علائم حیاتی و سلامت بدون زحمت مداوم.",
                    tag: "شارژدهی ۷ روزه",
                },
            ],
            guaranteeBadges: [
                "۱۸ ماه گارانتی رسمی دلبند",
                "ارسال رایگان کیت تعیین سایز",
                "تحلیل و راهنمای خواب فارسی در اپلیکیشن",
            ],
            marketVerdict: "بهترین انتخاب برای افرادی که اولویت اصلی آن‌ها پایش علمی کیفیت خواب، ریکاوری و آرامش شبانه است.",
        },
    },
    {
        id: "EVR-XE15",
        model: "EVR-XE15",
        category: "حلقه پرچمدار تیتانیوم گرید ۵ با نوار قلب ECG",
        tagline: "ثبت الکتروکاردیوگرام ECG، روند گردش خون، دمای پایه بدن با دقت ±۰.۰۵°C و کیس شارژ مسافرتی ۳۰ روزه",
        description:
            "شاهکار مهندسی با آلیاژ تیتانیوم گرید ۵ هوافضا و تراشه‌های پیشرفته Goodix و Nordic، مجهز به الکترودهای ECG نوک انگشت، پایش روند گردش خون، سنسور دمای پایه بدن با دقت آزمایشگاهی، مقاومت آب 5ATM و کیس شارژ پرتابل با ۳۰ روز استقلال انرژی بدون نیاز به پریز برق.",
        image: "/media/products/rings/EVR-XE15.webp",
        accentColor: "#0038A8",
        bgGradient: "from-blue-100/40 via-indigo-50/30 to-white",
        idealFor: "مدیران و افرادی که به دنبال نهایت ظرافت، متریال لوکس، نوار قلب ECG و پایش بی‌وقفه سلامت هستند.",
        hotspots: [
            { x: 42, y: 35, title: "الکترود تیتانیومی ECG", desc: "ثبت الکتروکاردیوگرام از طریق نوک انگشتان دست" },
            { x: 55, y: 55, title: "حسگر دمای پایه بدن (±۰.۰۵°C)", desc: "اندازه‌گیری حرارت مویرگی با دقت آزمایشگاهی" },
            { x: 62, y: 75, title: "کیس شارژ پرتابل مسافرتی", desc: "شارژ مجدد تا ۳۰ روز بدون نیاز به برق مستقیم" },
        ],
        quickStats: [
            { label: "متریال بدنه", value: "تیتانیوم گرید ۵", subtext: "فوق‌العاده مقاوم و سبک", icon: "weight" },
            { label: "سنسور قلب", value: "ECG + گردش خون", subtext: "ثبت نوار قلب انگشتی", icon: "heart" },
            { label: "مقاومت آب", value: "5ATM (۵۰m)", subtext: "ایمن برای شنا و استخر", icon: "water" },
            { label: "کیس شارژ", value: "۳۰ روزه", subtext: "شارژ پرتابل در حرکت", icon: "battery" },
        ],
        specs: {
            battery: { title: "۷ روز مستقیم (۳۰ روز با کیس)", desc: "کیس شارژ شیک پرتابل که حلقه را تا ۵ بار کامل شارژ می‌کند" },
            sensors: { title: "الکترودهای ECG + روند گردش خون + دما", desc: "ثبت نوار قلب، دمای پایه پوست، HRV و غلظت اکسیژن خون" },
            material: { title: "تیتانیوم گرید ۵ + رزین آنتی‌باکتریال", desc: "سطح صیقلی سازگار با پوست بدون ایجاد هرگونه حساسیت" },
            connectivity: { title: "تراشه Nordic + بلوتوث ۵.۲", desc: "انتقال امن و رمزنگاری‌شده داده‌ها به اپلیکیشن دلبند" },
        },
        fullSpecsTable: [
            {
                title: "آلیاژ ساخت، تراشه‌ها و متریال لوکس",
                items: [
                    { key: "بدنه بیرونی", val: "تیتانیوم گرید ۵ هوافضایی با پوشش فیزیکی بخار (PVD) ضدخش" },
                    { key: "تراشه‌های پردازشی", val: "چیپست‌های بیومتریک Goodix 3228T و Nordic با دقت پردازش عالی" },
                    { key: "پوشش داخلی", val: "رزین اپوکسی آنتی‌باکتریال بدون درز و ۱۰۰٪ ضدحساسیت پوستی" },
                    { key: "مقاومت در برابر آب", val: "استاندارد 5ATM (مقاوم تا عمق ۵۰ متری آب، مناسب استخر و شستشو)" },
                ],
            },
            {
                title: "سنسورهای پیشرفته و سیستم شارژ",
                items: [
                    { key: "ثبت ECG انگشتی", val: "الکترودهای تماسی دوقطبی با تفکیک امواج میلی‌ولتی و خروجی استاندارد" },
                    { key: "روند گردش خون و حرارت", val: "برآورد روند گردش خون و دماسنج پیوسته با دقت ±۰.۰۵°C" },
                    { key: "کیس شارژ پرتابل", val: "کیس لوکس همراه با باتری داخلی (تا ۳۰ روز استقلال شارژ کامل)" },
                ],
            },
        ],
        marketPresentation: {
            headline: "تلفیق شکوه مهندسی تیتانیوم با پیشرفته‌ترین پایشگر سلامت انگشتی جهان",
            uspTitle: "۳ ویژگی منحصربه‌فرد EVR-XE15",
            uspItems: [
                {
                    title: "متریال تیتانیوم گرید ۵ هوافضا",
                    description: "فوق‌العاده مقاوم در برابر خط و خش، سبک‌تر از فلزات دیگر و با درخشندگی مات و پرستیژ بالا.",
                    tag: "لوکس و بادوام",
                },
                {
                    title: "ثبت نوار قلب ECG و دمای پایه بدن",
                    description: "الکترودهای تماسی انگشتی برای بررسی ریتم قلب و سنسور حرارتی دقیق ±۰.۰۵°C برای رصد سلامت.",
                    tag: "تشخیص بالینی",
                },
                {
                    title: "کیس شارژ پرتابل با استقلال ۳۰ روزه",
                    description: "کیس مسافرتی لوکس شبیه هندزفری که حلقه را تا ۵ بار بدون نیاز به پریز برق شارژ می‌کند.",
                    tag: "استقلال شارژ ۳۰ روزه",
                },
            ],
            guaranteeBadges: [
                "۲۴ ماه گارانتی VIP تعویض بی‌قید و شرط",
                "کیت رایگان سایزبندی انگشت پیش از ارسال نهایی",
                "بسته‌بندی پرچمدار و کیس شارژ مسافرتی فاخر",
            ],
            marketVerdict: "لوکس‌ترین و کامل‌ترین حلقه هوشمند بازار برای مدیران، ورزشکاران و شیفتگان کیفیت ممتاز.",
        },
    },
];

// ─── English Products ────────────────────────────────────────────────────────
const BAND_PRODUCTS_EN: CuratedProduct[] = [
    {
        id: "EVB-X8",
        model: "EVB-X8",
        category: "High-Precision Multi-Sensor Flagship",
        tagline: "512Hz ECG recording, non-invasive metabolic & circulation trends, and continuous skin temp",
        description:
            "Delband's most advanced screen-free health band offering 512Hz ECG recording, continuous circulation and metabolic trend estimation, continuous skin temperature, IP68 water resistance, and 15+ days battery life.",
        image: "/media/products/band/EVB-X8.webp",
        accentColor: "#0038A8",
        bgGradient: "from-blue-100/40 via-indigo-50/30 to-white",
        idealFor: "Users seeking precision heart and vitals monitoring, energy and metabolic trend analysis, and comprehensive sleep tracking.",
        hotspots: [
            { x: 50, y: 32, title: "512Hz ECG Electrode", desc: "High-frequency standard ECG waveform recording" },
            { x: 45, y: 60, title: "Dual PPG Optical Sensor", desc: "Non-invasive energy and circulation trend tracking" },
            { x: 65, y: 76, title: "Continuous Skin Temp", desc: "Epidermal temp monitoring with ±0.05°C precision" },
        ],
        quickStats: [
            { label: "ECG Sampling", value: "512 Hz", subtext: "Standard waveform recording", icon: "heart" },
            { label: "Battery Life", value: "15+ Days", subtext: "24/7 continuous monitoring", icon: "battery" },
            { label: "Vitals Tracked", value: "8 Sensors", subtext: "ECG, SpO2, Temp, Metabolic & Circulation", icon: "weight" },
            { label: "Protection", value: "IP68", subtext: "Water & dust resistant", icon: "water" },
        ],
        specs: {
            battery: { title: "15+ Days Active Runtime", desc: "180mAh Li-Po battery with ultra-low power circuitry and fast magnetic charging" },
            sensors: { title: "ECG Chip + Circulation & Energy Sensor", desc: "Electrocardiogram, energy & circulation trend estimation, SpO2, HRV, and skin temp" },
            material: { title: "Reinforced Polycarbonate + Woven Straps", desc: "Hypoallergenic skin-friendly body with breathable woven and silicone straps" },
            connectivity: { title: "Bluetooth 5.3 + Cloud Sync", desc: "Auto-sync with Delband Health App, family sharing, and wellness reports" },
        },
        fullSpecsTable: [
            {
                title: "Hardware & Sensor Suite",
                items: [
                    { key: "ECG Bio-Chip", val: "512Hz high-resolution contact electrodes with standard waveform export" },
                    { key: "Optical Sensors", val: "Dual-wavelength Red & Infrared PPG channels with SpO2 and HRV" },
                    { key: "Circulation & Energy Trend", val: "Advanced hemodynamic and optical waveform trend estimation" },
                    { key: "Thermal Sensor", val: "Epidermal temperature tracking with ±0.05°C high precision" },
                ],
            },
            {
                title: "Battery, Housing & Connectivity",
                items: [
                    { key: "Battery Capacity", val: "180mAh High-Density Li-Po (15+ days active runtime)" },
                    { key: "Charging Time", val: "60 minutes fast magnetic charging" },
                    { key: "Ingress Rating", val: "IP68 water, shower, and swim resistant" },
                    { key: "OS Compatibility", val: "Android & iOS with full Delband App integration" },
                ],
            },
        ],
        marketPresentation: {
            headline: "Your personal wellness & vitals companion on your wrist",
            uspTitle: "Key Advantages of EVB-X8",
            uspItems: [
                {
                    title: "24/7 Screen-Free Wellness",
                    description: "No distracting notifications, promoting calmer focus while recording all vitals with high accuracy.",
                    tag: "Pure Focus",
                },
                {
                    title: "512Hz High-Precision ECG",
                    description: "High-resolution cardiac recording with instant PDF export for personal fitness tracking.",
                    tag: "High Precision",
                },
                {
                    title: "Full Wellness Sensor Suite",
                    description: "Combines ECG, metabolic and circulation trends, skin temperature, and SpO2 in a lightweight band.",
                    tag: "Full-Suite Vitals",
                },
            ],
            guaranteeBadges: [
                "18-Month Replacement Warranty",
                "7-Day Unconditional Money-Back Guarantee",
                "Lifetime Software & Cloud Analytics Support",
            ],
            marketVerdict: "The ultimate choice for users who want dedicated, discreet, and highly accurate cardiac, energy, and vitals tracking.",
        },
    },
    {
        id: "EVB-E50",
        model: "EVB-E50",
        category: "Electrode ECG & Zinc Alloy Health Band",
        tagline: "Electrode ECG recording, circulation & energy trends with premium Zinc Alloy chassis and 14-day battery",
        description:
            "A premium metal-bodied screenless health band combining durable Zinc Alloy and polycarbonate (Zinc Alloy + PC); equipped with contact ECG electrodes and dual PPG optical sensors for continuous cardiac, circulation trends, energy indicators, and sleep monitoring with 7–14 days runtime (30+ days standby).",
        image: "/media/hero-band/band3.webp",
        accentColor: "#0D9488",
        bgGradient: "from-teal-100/40 via-emerald-50/30 to-white",
        idealFor: "Users desiring high-end metallic build quality, precision ECG, cardiovascular vitals tracking, and zero notification distractions.",
        hotspots: [
            { x: 50, y: 35, title: "Contact ECG Electrode", desc: "Touch-activated electrocardiogram recording" },
            { x: 45, y: 62, title: "Dual PPG Optical Sensor", desc: "Continuous SpO2, circulation trends, and heart rate" },
            { x: 65, y: 78, title: "Zinc Alloy Metallic Body", desc: "Scratch-resistant, impact-proof & hypoallergenic" },
        ],
        quickStats: [
            { label: "ECG Sensor", value: "Electrode", subtext: "Accurate ECG recording", icon: "heart" },
            { label: "Battery Life", value: "14 Days", subtext: "Standby up to 30+ days", icon: "battery" },
            { label: "Material", value: "Zinc Alloy", subtext: "Zinc Alloy + PC chassis", icon: "weight" },
            { label: "Protection", value: "IP68", subtext: "Water & sweat resistant", icon: "water" },
        ],
        specs: {
            battery: { title: "7 to 14 Days Runtime (30+ Days Standby)", desc: "130mAh battery with ultra-low power consumption due to screenless architecture" },
            sensors: { title: "ECG Electrodes + Dual PPG", desc: "Electrocardiogram, circulation trend monitoring, non-invasive energy trend, skin temp, and sleep" },
            material: { title: "Zinc Alloy + Compact PC", desc: "Durable metallic body paired with breathable woven and silicone straps" },
            connectivity: { title: "Bluetooth 5.0 Low Energy", desc: "Stable connection and automatic sync to Delband Health App" },
        },
        fullSpecsTable: [
            {
                title: "Sensors & Wellness Hardware",
                items: [
                    { key: "ECG Hardware", val: "Side-mounted contact electrodes with biometric signal filter" },
                    { key: "Circulation & Energy Trend", val: "Non-invasive optical pulse-wave velocity and energy trend tracking" },
                    { key: "Thermal Sensor", val: "Continuous epidermal temperature tracking" },
                    { key: "Sleep Diagnostics", val: "Deep/Light/REM sleep stages and respiration rate" },
                ],
            },
            {
                title: "Build & Battery Specifications",
                items: [
                    { key: "Housing Material", val: "Durable Zinc Alloy top cover + PC base" },
                    { key: "Battery Capacity", val: "130mAh Li-Po with fast magnetic charger" },
                    { key: "Ingress Rating", val: "IP68 certified waterproof and sweatproof" },
                    { key: "Women's Health", val: "Full menstrual cycle, ovulation, and symptom logging" },
                ],
            },
        ],
        marketPresentation: {
            headline: "Solid metal durability meets cardiovascular wellness tracking",
            uspTitle: "Distinctive Advantages of EVB-E50",
            uspItems: [
                {
                    title: "Electrode ECG & 24/7 Vitals",
                    description: "Effortless ECG recording by touching the metal frame electrodes.",
                    tag: "Contact Electrodes",
                },
                {
                    title: "Premium Metallic Zinc Alloy Frame",
                    description: "Hypoallergenic metal casing ensuring superior durability and a sleek, premium feel.",
                    tag: "Metallic Build",
                },
                {
                    title: "Long 14-Day Battery Runtime",
                    description: "Two weeks of active continuous tracking and up to 30 days on standby.",
                    tag: "Long Battery",
                },
            ],
            guaranteeBadges: [
                "18-Month Official Warranty",
                "7-Day Free Trial & Replacement",
                "Full Multilingual App & Technical Support",
            ],
            marketVerdict: "A stylish and rugged health band for users who want electrode ECG, metallic durability, and long battery life.",
        },
    },
    {
        id: "EVB-X5",
        model: "EVB-X5",
        category: "Extended Power Autonomy with Travel Dock",
        tagline: "30-day endurance with portable smart dock, deep sleep and stress analytics",
        description:
            "Built for active lifestyles and frequent travelers; features a 210mAh magnetic dock that eliminates the need for wall outlets for an entire month; equipped with 24/7 heart rate, SpO2, blood pressure, skin temperature, and sleep analytics.",
        image: "/media/products/band/band-4.webp",
        accentColor: "#0284C7",
        bgGradient: "from-cyan-100/40 via-blue-50/30 to-white",
        idealFor: "Travelers, outdoor enthusiasts, and professionals prioritizing ultra-long battery life and deep sleep recovery.",
        hotspots: [
            { x: 50, y: 35, title: "210mAh Smart Dock", desc: "Portable charging case providing 30 days total runtime" },
            { x: 45, y: 60, title: "Sleep Lab Sensor", desc: "Precise tracking of Deep, Light, and REM cycles" },
            { x: 65, y: 75, title: "Medical Silicone", desc: "Resistant to sweat, UV rays, and repeated washing" },
        ],
        quickStats: [
            { label: "Dock Runtime", value: "30 Days", subtext: "With 210mAh portable dock", icon: "battery" },
            { label: "Sleep Analysis", value: "4 Stages", subtext: "Light, Deep, REM & Awake", icon: "heart" },
            { label: "Waterproof", value: "IP68", subtext: "Shower and swim proof", icon: "water" },
            { label: "Stress Tracking", value: "24/7", subtext: "Guided breathwork exercises", icon: "weight" },
        ],
        specs: {
            battery: { title: "30 Days with Travel Dock", desc: "Pocket-sized magnetic dock acting as dedicated powerbank" },
            sensors: { title: "Dual PPG with Noise Filter", desc: "Nocturnal SpO2 tracking and sleep apnea anomaly detection" },
            material: { title: "Medical-Grade Silicone", desc: "Hypoallergenic, sweat-resistant, UV-protected strap" },
            connectivity: { title: "Offline Storage", desc: "Stores 7 days of biometric data offline" },
        },
        fullSpecsTable: [
            {
                title: "Charging System & Dock",
                items: [
                    { key: "Portable Dock", val: "210mAh magnetic case for on-the-go recharges" },
                    { key: "Total Endurance", val: "Up to 30 days without wall outlet power" },
                    { key: "Input Interface", val: "Modern USB Type-C with overcharge protection" },
                ],
            },
            {
                title: "Sleep & Respiratory Analysis",
                items: [
                    { key: "Sleep Staging", val: "Deep, Light, REM, and micro-awakenings" },
                    { key: "Apnea Monitoring", val: "Nocturnal blood oxygen dips and breathing rate" },
                    { key: "Internal Buffer", val: "7-day onboard memory without smartphone sync" },
                ],
            },
        ],
        marketPresentation: {
            headline: "Complete freedom for travel and outdoor life with 1-month battery autonomy",
            uspTitle: "Why Travelers & Hikers Love EVB-X5",
            uspItems: [
                {
                    title: "Pocket Magnetic Charging Dock",
                    description: "Never worry about dead batteries on camping trips, flights, or long journeys.",
                    tag: "Total Autonomy",
                },
                {
                    title: "Clinical-Level Sleep Lab Analytics",
                    description: "Accurate sleep scoring and nocturnal breathing assessment.",
                    tag: "Deep Recovery",
                },
                {
                    title: "Built for Harsh Outdoor Conditions",
                    description: "IP68 water resistance and rugged medical-grade silicone.",
                    tag: "Adventure Ready",
                },
            ],
            guaranteeBadges: [
                "18-Month Delband Warranty",
                "Full Nationwide Technical Support",
                "Luxury Travel Case & Dock Included",
            ],
            marketVerdict: "The best choice for busy professionals and outdoor athletes who value battery independence.",
        },
    },
    {
        id: "EVB-1s",
        model: "EVB-1s",
        category: "AI Health Coach, Strain & Recovery Band",
        tagline: "AI Strain & Recovery Coach, VO2Max tracking, and deep sleep sleep apnea analysis",
        description:
            "Next-generation ultra-slim screenless health band (42 x 22 x 9.5 mm) powered by AI algorithms to calculate daily cardiovascular strain, recovery score, deep/REM sleep staging, sleep apnea anomalies, VO2Max, skin temperature, and AI mood tracking.",
        image: "/media/hero-band/EVB-1s.webp",
        accentColor: "#6366F1",
        bgGradient: "from-indigo-100/40 via-purple-50/30 to-white",
        idealFor: "Professional athletes, biohackers, and anyone dedicated to optimizing daily energy, restorative sleep, and nervous system recovery.",
        hotspots: [
            { x: 50, y: 35, title: "AI Recovery Engine", desc: "Calculates daily training Strain and Readiness score" },
            { x: 45, y: 62, title: "Optical & Temp Sensors", desc: "Continuous heart rate, SpO2, HRV, and skin temp" },
            { x: 65, y: 76, title: "Elastic Woven Band", desc: "Featherlight, breathable, and zero wrist pressure" },
        ],
        quickStats: [
            { label: "AI Recovery", value: "AI Engine", subtext: "Calculates Strain, Readiness & Mood", icon: "heart" },
            { label: "Battery Life", value: "7 to 10 Days", subtext: "Standby 25+ days", icon: "battery" },
            { label: "Advanced Metrics", value: "VO2Max", subtext: "Sleep staging, apnea & temp", icon: "weight" },
            { label: "Protection", value: "IP68", subtext: "Water & sweat resistant", icon: "water" },
        ],
        specs: {
            battery: { title: "7 to 10 Days Active Use", desc: "95mAh Li-Po battery with 60-minute fast magnetic charge" },
            sensors: { title: "AI-Powered Biosensors", desc: "Continuous heart rate, HRV, SpO2, skin temp, VO2Max, and sleep apnea" },
            material: { title: "Ergonomic PC+ABS Chassis", desc: "Featherlight 42 x 22 x 9.5 mm profile with woven elastic strap" },
            connectivity: { title: "Bluetooth 5.0 Low Energy", desc: "Seamless app sync with dedicated recovery coaching analytics" },
        },
        fullSpecsTable: [
            {
                title: "AI Analytics & Biosensors",
                items: [
                    { key: "Strain & Recovery Engine", val: "AI algorithm tracking cardiovascular exertion and nocturnal readiness" },
                    { key: "VO2Max Aerobic Metric", val: "Cardiorespiratory fitness estimation and oxygen uptake efficiency" },
                    { key: "Sleep & Apnea Diagnostics", val: "Deep/REM/Light sleep staging and nocturnal oxygen dip detection" },
                    { key: "Stress & AI Mood", val: "HRV-based autonomic nervous balance and mood trend tracking" },
                ],
            },
            {
                title: "Dimensions, Build & Power",
                items: [
                    { key: "Dimensions", val: "42 x 22 x 9.5 mm (Ultra-slim, low-profile form factor)" },
                    { key: "Battery Capacity", val: "95mAh Li-Po (7-10 days runtime, 25+ days standby)" },
                    { key: "Ingress Protection", val: "IP68 certified water and dust resistance" },
                    { key: "Women's Health", val: "Comprehensive cycle, ovulation, and recovery tracking" },
                ],
            },
        ],
        marketPresentation: {
            headline: "Your personal AI recovery coach for peak physical and mental performance",
            uspTitle: "Key Advantages of EVB-1s for Athletes & Biohackers",
            uspItems: [
                {
                    title: "Advanced Strain & Readiness Calculation",
                    description: "Matches daily workout load with overnight sleep quality to recommend the optimal training intensity.",
                    tag: "AI Coach",
                },
                {
                    title: "Deep Sleep & Sleep Apnea Analysis",
                    description: "Tracks REM and deep sleep cycles with nocturnal oxygen drops and breathing disruptions.",
                    tag: "Sleep Science",
                },
                {
                    title: "Featherweight & Ergonomic 9.5mm Body",
                    description: "At only 9.5mm thin with an elastic woven band, it sits weightlessly on your wrist day and night.",
                    tag: "Zero-Bulk Design",
                },
            ],
            guaranteeBadges: [
                "18-Month Official Warranty",
                "Free Express Shipping & 7-Day Trial",
                "Syncs with Major Health & Fitness Platforms",
            ],
            marketVerdict: "The best choice for athletes and health optimizers wanting AI recovery insights, strain tracking, and VO2Max.",
        },
    },
];

const RING_PRODUCTS_EN: CuratedProduct[] = [
    {
        id: "EVR-X5",
        model: "EVR-X5",
        category: "Smart Touch-Control Ring with Zirconia Ceramic & Titanium",
        tagline: "Smart Touch control gestures, non-invasive energy & SpO2 tracking, scratch-proof ceramic chassis & fast wireless charging",
        description:
            "Pioneering ultra-slim 2.2mm smart ring combining nano-zirconia ceramic and titanium; equipped with smart touch control for effortless device interaction, 24/7 heart rate, SpO2, HRV, non-invasive metabolic trend assessment, skin temperature, 54+ sports modes, and 5-10 days battery life.",
        image: "/media/products/rings/EVR-X5.webp",
        accentColor: "#D97706",
        bgGradient: "from-amber-100/40 via-orange-50/30 to-white",
        idealFor: "Tech innovators, presentation and media creators, and wellness enthusiasts wanting smart gesture control and luxury ceramic design.",
        hotspots: [
            { x: 50, y: 32, title: "Smart Touch Sensor", desc: "Swipe to flip short videos, switch presentation slides, or trigger camera shutter" },
            { x: 46, y: 60, title: "Dual Optical PPG & SpO2", desc: "Continuous heart rate, blood oxygen, HRV, and energy trend analysis" },
            { x: 64, y: 76, title: "Nano Zirconia Ceramic Shell", desc: "Diamond-hard scratch resistance, mirror luster, and 5ATM waterproof" },
        ],
        quickStats: [
            { label: "Smart Touch", value: "Gestures", subtext: "Swipe, tap & control", icon: "weight" },
            { label: "Thickness", value: "2.2 mm", subtext: "Ultra-slim luxury profile", icon: "heart" },
            { label: "Waterproof", value: "5ATM", subtext: "Pool and swim safe up to 50m", icon: "water" },
            { label: "Battery Life", value: "5-10 Days", subtext: "Rapid wireless inductive charge", icon: "battery" },
        ],
        specs: {
            battery: { title: "5 to 10 Days Active Endurance", desc: "Magnetic wireless induction charging pad under 90 minutes" },
            sensors: { title: "Touch Sensor + Dual PPG + Temp", desc: "Heart rate, SpO2, non-invasive energy trend, HRV & skin temp" },
            material: { title: "Polished Zirconia Ceramic + Ti", desc: "Antibacterial inner resin ensuring 100% skin safety" },
            connectivity: { title: "Low-Energy Bluetooth 5.1", desc: "Instant phone pairing, media control & Delband cloud sync" },
        },
        fullSpecsTable: [
            {
                title: "Touch Gestures, Sensors & Processing",
                items: [
                    { key: "Smart Touch Control", val: "Capacitive surface sensor for video scroll, slide deck flips & camera trigger" },
                    { key: "Biometric Optical PPG", val: "Dual-channel infrared & red LED array for SpO2, heart rate & HRV" },
                    { key: "Non-Invasive Energy Trend", val: "Photoplethysmography-based algorithmic metabolic trend assessment" },
                    { key: "Sports Modes & VO2Max", val: "54+ specialized sports disciplines with aerobic VO2Max estimation" },
                ],
            },
            {
                title: "Chassis Design, Battery & Protection",
                items: [
                    { key: "Shell Material", val: "Nano-crystal Zirconia Ceramic (scratch-proof diamond hardness) & Titanium" },
                    { key: "Dimensions", val: "Thickness 2.2mm | Width 8.0mm (ultra-slim ergonomic comfort)" },
                    { key: "Water Resistance", val: "5ATM certified (submersible up to 50 meters depth)" },
                    { key: "Battery & Charging", val: "Li-Po battery with 5-10 days endurance; wireless magnetic pad included" },
                ],
            },
        ],
        marketPresentation: {
            headline: "Magical touch interaction with your digital universe in an ultra-slim ceramic ring",
            uspTitle: "Unrivaled Advantages of EVR-X5",
            uspItems: [
                {
                    title: "Smart Touch Control Gestures",
                    description: "Swipe or tap on the ring surface to change slides, scroll video feeds, or capture photos hands-free.",
                    tag: "Smart Touch",
                },
                {
                    title: "Ultra-Thin 2.2mm Ceramic Architecture",
                    description: "One of the slimmest smart rings in the world, gleaming on your finger like luxury jewelry.",
                    tag: "Ultra-Slim 2.2mm",
                },
                {
                    title: "24/7 Metabolic Trend & 54+ Sports Modes",
                    description: "Comprehensive metabolic monitoring, HRV, skin temperature, and VO2Max in a single discreet wearable.",
                    tag: "Holistic Health",
                },
            ],
            guaranteeBadges: [
                "18-Month Official Warranty",
                "Free Finger Sizing Kit Included",
                "Fast Wireless Desktop Charger In The Box",
            ],
            marketVerdict: "The best choice for users wanting innovative touch gestures, ceramic jewelry elegance, and complete health tracking.",
        },
    },
    {
        id: "EVR-X3",
        model: "EVR-X3",
        category: "High-Precision SpO2 Transmission & Sleep Respiration Monitoring",
        tagline: "Transmission SpO2 sensor (±2.7% accuracy), sleep respiration index, aerospace titanium & non-invasive energy trend",
        description:
            "Specialized EVR-X3 smart ring crafted from aerospace titanium alloy weighing only 3.4g with a 2.7mm profile; features a high-precision SpO2 transmission sensor, sleep respiration index, non-invasive energy trends, 5ATM waterproofing, and 7+ days battery life with rapid wireless charging under 90 minutes.",
        image: "/media/products/rings/EVR-X3.webp",
        accentColor: "#0284C7",
        bgGradient: "from-cyan-100/40 via-blue-50/30 to-white",
        idealFor: "Snorers, individuals monitoring sleep breathing quality, athletes, and wellness enthusiasts demanding high oxygen accuracy.",
        hotspots: [
            { x: 48, y: 34, title: "Transmission SpO2 Sensor", desc: "High-accuracy (±2.7%) blood oxygen transmission and nocturnal breathing alerts" },
            { x: 52, y: 60, title: "Energy Trend Module", desc: "Non-invasive glycemic trend assessment and metabolic health indicators" },
            { x: 62, y: 76, title: "Aerospace Titanium Chassis", desc: "Weighs only 3.4g, scratch-resistant, hypoallergenic and 5ATM waterproof" },
        ],
        quickStats: [
            { label: "SpO2 Precision", value: "±2.7%", subtext: "High-precision transmission sensor", icon: "heart" },
            { label: "Net Weight", value: "3.4g", subtext: "Ultra-slim 2.7mm thickness", icon: "weight" },
            { label: "Waterproof", value: "5ATM", subtext: "Swim & pool safe to 50m", icon: "water" },
            { label: "Battery Life", value: "7+ Days", subtext: "Fast wireless charging dock", icon: "battery" },
        ],
        specs: {
            battery: { title: "Over 7 Days Continuous Runtime", desc: "Fast inductive wireless charging in under 90 minutes" },
            sensors: { title: "Transmission SpO2 + Energy Trend", desc: "Oxygen sensor, sleep respiration index, skin temp & HRV" },
            material: { title: "Aerospace Grade Titanium Alloy", desc: "Lightweight, highly resilient, with antibacterial resin inner liner" },
            connectivity: { title: "Stable Bluetooth 5.2 Link", desc: "Encrypted background sync and instant breathing alerts" },
        },
        fullSpecsTable: [
            {
                title: "Advanced Sensors & Respiration Analysis",
                items: [
                    { key: "Transmission SpO2", val: "Through-tissue light transmission sensor delivering ±2.7% high precision" },
                    { key: "Sleep Respiration Index", val: "Real-time detection of overnight desaturations and respiratory interruptions" },
                    { key: "Energy Trend", val: "Continuous non-invasive metabolic trend estimation based on optical PPG signals" },
                    { key: "Continuous Skin Temp", val: "Constant temperature tracking for early biological anomaly detection" },
                ],
            },
            {
                title: "Technical Build, Durability & Sports",
                items: [
                    { key: "Chassis Material", val: "Aerospace titanium alloy with hypoallergenic seamless antibacterial resin interior" },
                    { key: "Dimensions & Weight", val: "Weight 3.4g | Thickness 2.7mm (light and zero-friction ergonomics)" },
                    { key: "Water Resistance", val: "5ATM certified (50 meters water depth rating for swimming)" },
                    { key: "Sports Tracking", val: "54+ smart sports modes with real-time VO2Max evaluation" },
                ],
            },
        ],
        marketPresentation: {
            headline: "The world's most precise blood oxygen & nocturnal respiratory health smart ring",
            uspTitle: "Distinct Advantages of EVR-X3",
            uspItems: [
                {
                    title: "High-Precision Transmission SpO2 Sensor",
                    description: "Unlike reflective sensors, transmission technology passes light directly through capillary tissue for exceptional precision.",
                    tag: "High Precision",
                },
                {
                    title: "Nocturnal Sleep Breathing Warning",
                    description: "Monitors oxygen drops during sleep to alert against breathing irregularities.",
                    tag: "Sleep Safety",
                },
                {
                    title: "Featherweight 3.4g Titanium with 7-Day Battery",
                    description: "Ultra-slim 2.7mm profile with fast 90-minute wireless charging for zero-effort weekly tracking.",
                    tag: "Ultra-Light 3.4g",
                },
            ],
            guaranteeBadges: [
                "18-Month Gold Warranty",
                "Free Sizing Kit Before Delivery",
                "In-Depth Sleep & Respiration App Analytics",
            ],
            marketVerdict: "The most specialized smart ring for blood oxygen precision, sleep respiration awareness, and vital wellness.",
        },
    },
    {
        id: "EVR-X2",
        model: "EVR-X2",
        category: "Sleep Lab Diagnostics & Circadian Recovery Tracking",
        tagline: "Dedicated nocturnal infrared PPG, Deep/REM sleep staging, HRV recovery score & lightweight titanium build",
        description:
            "Ergonomically optimized EVR-X2 titanium smart ring built specifically for continuous nocturnal sleep and recovery tracking; captures deep, light, and REM sleep cycles, skin temperature, nocturnal SpO2 dips, 54+ sports modes, and 5-7 days battery endurance.",
        image: "/media/products/rings/EVR-X2.webp",
        accentColor: "#6366F1",
        bgGradient: "from-indigo-100/40 via-purple-50/30 to-white",
        idealFor: "Biohackers, individuals seeking deeper restorative sleep, nervous system recovery, and a completely invisible infrared sleep sensor.",
        hotspots: [
            { x: 48, y: 35, title: "Invisible Infrared Sleep Module", desc: "Nocturnal biometrics without visible green flashing lights disturbing sleep" },
            { x: 54, y: 58, title: "Skin Temperature & HRV", desc: "Daily biological readiness, autonomic recovery, and menstrual cycle forecasting" },
            { x: 62, y: 76, title: "Ergonomic Titanium Shell", desc: "8.0mm width and 3.0g weight ensuring zero bulk during sleep" },
        ],
        quickStats: [
            { label: "Sleep Analysis", value: "Sleep Lab", subtext: "4 sleep stages + Recovery score", icon: "heart" },
            { label: "Chassis", value: "Titanium", subtext: "Lightweight, durable & skin-safe", icon: "weight" },
            { label: "Waterproof", value: "5ATM", subtext: "Submersible up to 50m", icon: "water" },
            { label: "Battery Life", value: "5-7 Days", subtext: "Continuous 24/7 bio-logging", icon: "battery" },
        ],
        specs: {
            battery: { title: "5 to 7 Days Round-The-Clock Life", desc: "Fast magnetic wireless charging pad with optimized power draw" },
            sensors: { title: "Infrared PPG Module + Skin Temp", desc: "Deep/REM sleep staging, HRV, nocturnal SpO2, and pulse" },
            material: { title: "Polished Lightweight Titanium", desc: "100% hypoallergenic inner resin suitable for sensitive skin" },
            connectivity: { title: "Low Energy Bluetooth Sync", desc: "Silent automatic sync without opening the app continuously" },
        },
        fullSpecsTable: [
            {
                title: "Sleep Diagnostics & Autonomic Recovery",
                items: [
                    { key: "Sleep Stage Analysis", val: "Logs Light Sleep, Deep Sleep, REM cycles, and nocturnal wakefulness" },
                    { key: "Recovery & Readiness Score", val: "Integrates sleep quality, nocturnal HRV, and resting pulse for daily guidance" },
                    { key: "Nocturnal SpO2 Trends", val: "Monitors oxygen drops and respiration rate across all sleep cycles" },
                    { key: "Women's Health Cycle", val: "Nocturnal temperature tracking for cycle and fertility window prediction" },
                ],
            },
            {
                title: "Ergonomic Architecture, Battery & Durability",
                items: [
                    { key: "Chassis Material", val: "Polished aerospace titanium alloy in Black, Silver, Space Gray, and Gold" },
                    { key: "Dimensions & Weight", val: "Width 8.0mm | Featherweight ~3.0g" },
                    { key: "Water Resistance", val: "5ATM rating (shower, pool, swimming, and rain resistant)" },
                    { key: "Fitness Capabilities", val: "54+ sports disciplines supported with VO2Max estimation" },
                ],
            },
        ],
        marketPresentation: {
            headline: "Your personal sleep laboratory in a sleek, ultra-comfortable ring",
            uspTitle: "Why EVR-X2 is the Premier Sleep Companion",
            uspItems: [
                {
                    title: "Zero Disturbance Invisible Infrared PPG",
                    description: "Unlike watches with bright flashing green LEDs, EVR-X2 uses invisible infrared light for undisturbed rest.",
                    tag: "Invisible Tracking",
                },
                {
                    title: "Comprehensive Deep, REM & Recovery Staging",
                    description: "Accurate sleep architecture scoring showing restorative deep sleep, cellular repair, and daily readiness.",
                    tag: "Sleep Architecture",
                },
                {
                    title: "Titanium Durability with 7-Day Battery",
                    description: "One charge per week covers all your days and nights without tedious daily charging routines.",
                    tag: "7-Day Endurance",
                },
            ],
            guaranteeBadges: [
                "18-Month Official Delband Warranty",
                "Free Finger Sizing Kit Before Dispatch",
                "Personalized Sleep Coaching in App",
            ],
            marketVerdict: "The best choice for users whose highest priority is scientific sleep tracking, nervous system recovery, and nightly comfort.",
        },
    },
    {
        id: "EVR-XE15",
        model: "EVR-XE15",
        category: "Aerospace Titanium Luxury Flagship with ECG",
        tagline: "Grade 5 Titanium crafting with simultaneous ECG, baseline temperature (±0.05°C), circulation trend and 30-day travel charging case",
        description:
            "A masterclass in precision engineering using aerospace Grade 5 titanium powered by Goodix and Nordic chips; equipped with contact ECG electrodes, circulation trend estimation, ±0.05°C basal body temperature, 5ATM waterproofing, and a portable travel case offering 30 days battery autonomy.",
        image: "/media/products/rings/EVR-XE15.webp",
        accentColor: "#0038A8",
        bgGradient: "from-blue-100/40 via-indigo-50/30 to-white",
        idealFor: "Executives and users seeking premium luxury materials, contact ECG recording, and continuous wellness tracking.",
        hotspots: [
            { x: 42, y: 35, title: "Titanium ECG Electrode", desc: "Fingertip electrocardiogram waveform capture" },
            { x: 55, y: 55, title: "Basal Body Temp Sensor (±0.05°C)", desc: "Micro-capillary thermal accuracy with high sensitivity" },
            { x: 62, y: 75, title: "Portable Travel Charging Case", desc: "Up to 30 days total on-the-go battery autonomy" },
        ],
        quickStats: [
            { label: "Material", value: "Grade 5 Titanium", subtext: "Ultra-durable aerospace alloy", icon: "weight" },
            { label: "Heart Sensor", value: "ECG + Circulation Trend", subtext: "Fingertip electrocardiogram", icon: "heart" },
            { label: "Waterproof", value: "5ATM (50m)", subtext: "Safe for swimming & pool", icon: "water" },
            { label: "Travel Case", value: "30-Day Total", subtext: "On-the-go multi-recharges", icon: "battery" },
        ],
        specs: {
            battery: { title: "7 Days Direct (30 Days with Case)", desc: "Includes luxury portable case recharging the ring up to 5 times" },
            sensors: { title: "ECG Electrodes + Circulation Trend + Temp", desc: "ECG waveform, basal temperature (±0.05°C), HRV, and SpO2" },
            material: { title: "Grade 5 Titanium + Antibacterial Resin", desc: "Seamless, antibacterial inner ring with zero allergic reaction" },
            connectivity: { title: "Nordic Chip + Bluetooth 5.2", desc: "Encrypted direct sync with Delband Health App" },
        },
        fullSpecsTable: [
            {
                title: "Luxury Materials & Build",
                items: [
                    { key: "Outer Shell", val: "Grade 5 Aerospace Titanium with anti-scratch PVD coating" },
                    { key: "Processing Chipsets", val: "Goodix 3228T & Nordic biometric processors with high-precision accuracy" },
                    { key: "Inner Liner", val: "Seamless antibacterial epoxy resin, 100% hypoallergenic" },
                    { key: "Water Resistance", val: "5ATM rating (up to 50 meters depth, pool & ocean safe)" },
                ],
            },
            {
                title: "Fingertip Bio-Sensors & Power",
                items: [
                    { key: "Fingertip ECG", val: "Bipolar contact electrodes capturing millivolt bio-potentials" },
                    { key: "Thermal & Circulation Trends", val: "Continuous infrared basal temperature calibration (±0.05°C) & circulation trends" },
                    { key: "Travel Dock Battery", val: "Travel dock offering 30 days total autonomy without wall outlets" },
                ],
            },
        ],
        marketPresentation: {
            headline: "Fusing aerospace titanium craftsmanship with the world's most advanced finger health sensor",
            uspTitle: "3 Distinct Features of EVR-XE15",
            uspItems: [
                {
                    title: "Grade 5 Aerospace Titanium Shell",
                    description: "Unmatched scratch resistance, lighter than steel, with a refined matte luster and high prestige.",
                    tag: "Luxury Durability",
                },
                {
                    title: "Fingertip ECG & ±0.05°C Basal Temperature",
                    description: "Contact electrodes for heart rhythm verification and high-sensitivity temperature calibration.",
                    tag: "Advanced Bio-Sensors",
                },
                {
                    title: "Earbud-Style Portable Charging Case",
                    description: "Elegant travel charging dock that recharges your ring up to 5 times without wall outlets.",
                    tag: "30-Day Autonomy",
                },
            ],
            guaranteeBadges: [
                "24-Month VIP Replacement Guarantee",
                "Free Sizing Kit Shipped Before Final Delivery",
                "Luxury Presentation Gift Box & Travel Case Included",
            ],
            marketVerdict: "The most luxurious and complete smart ring for executives and discerning quality enthusiasts.",
        },
    },
];

// ─── Helper to retrieve products by type & language ─────────────────────────
export function getCuratedProducts(productType: "band" | "ring" | "watch", lang: string): CuratedProduct[] {
    const isEn = lang?.startsWith("en");
    if (productType === "band") {
        return isEn ? BAND_PRODUCTS_EN : BAND_PRODUCTS_FA;
    }
    // Default to ring
    return isEn ? RING_PRODUCTS_EN : RING_PRODUCTS_FA;
}
